/**
 * ConsultantRdvTemplate — inspiré du modèle Wix « Consultant avec prise de rendez-vous » (wh-1059).
 * Fond crème, titres bleus appuyés, formes géométriques, cartes à contour. Textes et images propres à Grafton.
 */
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations; editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const CREME = "#F4F1EC", LAVANDE = "#B0B9F0", LIME = "#D6F76B";

const Etoile = ({ color }: { color: string }) => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round"><path d="M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9" /></svg>
);

const ConsultantRdvTemplate = ({ name, desc, product, city, customizations, editMode, onCustomize }: Props) => {
  const c = customizations || {};
  const bleu = c.primaryColor || "#2A3BF0";
  const set = (f: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(f, v);
  const prestations = [
    { t: c.productName || product || "Accompagnement stratégique", d: desc },
    { t: "Diagnostic de votre situation", d: "Un point clair sur où vous en êtes, ce qui fonctionne et ce qui freine votre développement." },
    { t: "Plan d'action sur mesure", d: "Des étapes concrètes, priorisées, avec des échéances que vous pouvez suivre." },
    { t: "Suivi dans la durée", d: "Des points réguliers pour mesurer les résultats et ajuster ensemble." },
  ];
  return (
    <div style={{ background: CREME, color: bleu, fontFamily: "'DM Sans', 'Poppins', sans-serif" }} data-testid="template-wix-consultant-rdv">
      <header className="px-6 md:px-14 py-6 flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 32 32" className="h-8 w-8"><circle cx="16" cy="8" r="6" fill={bleu} /><path d="M3 29 16 11l13 18z" fill={bleu} opacity=".9" /></svg>
          <EditableText as="span" value={name} onChange={set("heroTitle")} editMode={false} style={{ fontWeight: 600, fontSize: 17 }} />
        </div>
        <nav className="hidden md:flex gap-8 text-[15px]"><span>À propos</span><span>Prestations</span><span>Contact</span></nav>
      </header>

      <section className="relative overflow-hidden px-6 md:px-14 pt-10 pb-28 md:pb-40 min-h-[560px]">
        <div className="hidden md:block absolute right-0 top-0 w-[260px] h-[280px] rounded-bl-[260px]" style={{ background: LIME }} />
        <div className="hidden md:block absolute right-[300px] top-10 w-[170px] h-[240px] rounded-t-full rounded-br-full" style={{ background: "#DCDDF3" }} />
        <div className="hidden md:block absolute right-[130px] top-[300px] w-[170px] h-[170px]" style={{ background: "#DCDDF3" }} />
        <div className="hidden md:block absolute right-[300px] top-[290px] w-[170px] h-[170px] rounded-tr-[170px]" style={{ background: LIME }} />
        <div className="hidden md:block absolute right-0 top-[440px] w-[130px] h-[110px]" style={{ background: bleu }} />
        <div className="relative max-w-xl">
          <EditableText as="h1" value={c.heroTitle || name} onChange={set("heroTitle")} editMode={editMode} data-field="heroTitle"
            style={{ fontWeight: 700, fontSize: 46, lineHeight: 1.1, letterSpacing: "-0.01em" }} />
          <EditableText as="p" value={c.heroSubtitle || desc} onChange={set("heroSubtitle")} editMode={editMode} data-field="heroSubtitle"
            style={{ marginTop: 22, fontSize: 18, lineHeight: 1.55, opacity: 0.85 }} />
          <button className="mt-9 px-7 py-3 rounded-full border-2 text-[15px] font-medium" style={{ borderColor: bleu }}>
            <EditableText as="span" value={c.ctaText || "Prendre rendez-vous"} onChange={set("ctaText")} editMode={editMode} data-field="ctaText" />
          </button>
        </div>
        <div className="relative mt-16 grid md:grid-cols-2 gap-6 max-w-3xl">
          {prestations.slice(0, 2).map((p) => (
            <div key={p.t} className="rounded-2xl border-2 p-6" style={{ borderColor: bleu, background: CREME }}>
              <div className="font-bold text-[15px] mb-2">{p.t}</div><p className="text-[15px] leading-relaxed opacity-80">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-20" style={{ background: LAVANDE }}>
        <div className="max-w-2xl">
          <EditableText as="h2" value={c.serviceTitle || `Le parcours de ${name}`} onChange={set("serviceTitle")} editMode={editMode} data-field="serviceTitle" style={{ fontSize: 34, fontWeight: 700 }} />
          <EditableText as="p" value={c.testimonial || "Un accompagnement clair, des résultats concrets : voilà ce que nous cherchons à offrir à chaque client."} onChange={set("testimonial")} editMode={editMode} data-field="testimonial" style={{ marginTop: 18, fontSize: 17, lineHeight: 1.6 }} />
          <button className="mt-8 px-7 py-3 rounded-full border-2 text-[15px] font-medium" style={{ borderColor: bleu }}>{c.ctaSecondary || "À propos"}</button>
        </div>
      </section>

      <section className="px-6 md:px-14 py-20" style={{ background: bleu, color: "#fff" }}>
        <h2 style={{ fontSize: 34, fontWeight: 700 }}>Nos prestations</h2>
        <p className="mt-3 max-w-xl opacity-90" style={{ fontSize: 16, lineHeight: 1.6 }}>{city ? `Basés à ${city}, nous` : "Nous"} vous accompagnons de l'analyse jusqu'aux résultats.</p>
        <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl">
          {prestations.map((p, i) => (
            <div key={i} className="rounded-2xl border p-6" style={{ borderColor: "rgba(255,255,255,.75)" }}>
              <Etoile color={LIME} />
              <div className="mt-3 font-bold text-[15px]">{p.t}</div><p className="mt-2 text-[15px] leading-relaxed opacity-90">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-20 text-center" style={{ background: CREME }}>
        <EditableText as="h2" value={c.ctaFinalTitle || "Parlons de votre projet"} onChange={set("ctaFinalTitle")} editMode={editMode} data-field="ctaFinalTitle" style={{ fontSize: 34, fontWeight: 700 }} />
        <EditableText as="p" value={c.ctaFinalDesc || "Réservez un premier échange, sans engagement."} onChange={set("ctaFinalDesc")} editMode={editMode} data-field="ctaFinalDesc" style={{ marginTop: 14, fontSize: 17 }} />
        <button className="mt-8 px-8 py-3 rounded-full text-[15px] font-medium text-white" style={{ background: bleu }}>{c.ctaText || "Prendre rendez-vous"}</button>
      </section>

      <footer className="px-6 md:px-14 py-8 text-[14px] flex flex-wrap justify-between gap-3" style={{ background: LAVANDE }}>
        <span>{c.footerDesc || desc}</span><span>{c.email || ""} {c.phone || ""}</span>
      </footer>
    </div>
  );
};

export default ConsultantRdvTemplate;
